import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  WritableSignal,
  booleanAttribute,
  signal
} from '@angular/core';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { TiimeButtonModule, TiimeIconModule, TiimeTooltipModule } from 'tiime-material';

import { ContractStatus } from '@gi/enums/contract-status.enum';
import { DateInput } from '@helpers/date.helper';
import { BusinessUser } from '@models/business-user';
import { ContractReminderOverlayReturnedData } from '@shared-overlays/contract-reminder-overlay/contract-reminder-overlay-returned-data';
import {
  ContractReminderOverlayService,
  UpdateNextReminderDateData
} from '@shared-overlays/contract-reminder-overlay/contract-reminder-overlay.service';
import { ContractReminderLatePipe } from '@shared-pipes/contract-reminder-late/contract-reminder-late.pipe';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-contract-reminders-button',
  standalone: true,
  imports: [TiimeButtonModule, TiimeIconModule, TiimeTooltipModule, ContractReminderLatePipe, DatePipe],
  templateUrl: './contract-reminders-button.component.html',
  styleUrl: './contract-reminders-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.force-display]': 'forceDisplay()'
  }
})
export class ContractRemindersButtonComponent implements OnInit {
  @Input({ required: true }) contractId: number;
  @Input({ required: true }) contractStatus: ContractStatus;
  @Input({ required: true }) reminderCount: number;
  @Input({ required: true }) nextReminderDate: string;
  @Input({ required: true }) lastReminderDate: Date;
  @Input({ required: true }) nextReminderDateModifiedAt: Date;
  @Input({ required: true }) nextReminderDateModifiedBy: BusinessUser;
  @Input({ transform: booleanAttribute }) strocked: boolean = true;

  @Output() readonly addReminder: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() readonly updateNextReminderDate: EventEmitter<DateInput> = new EventEmitter<DateInput>();

  @ViewChild('overlayAnchor') overlayAnchor: ElementRef;

  readonly forceDisplay: WritableSignal<boolean> = signal<boolean>(false);

  private subscription: Subscription = new Subscription();

  constructor(private readonly contractReminderOverlayService: ContractReminderOverlayService) {}

  ngOnInit(): void {
    this.observeNextReminderDateChange();
  }

  openContractReminderOverlay(): void {
    this.forceDisplay.set(true);
    this.contractReminderOverlayService
      .open(
        {
          contractId: this.contractId,
          contractStatus: this.contractStatus,
          nextReminderDateModifiedAt: this.nextReminderDateModifiedAt,
          nextReminderDateModifiedBy: this.nextReminderDateModifiedBy
        },
        {
          connectTo: {
            origin: this.overlayAnchor,
            positions: [
              {
                originX: 'end',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
                offsetY: 5
              }
            ]
          }
        }
      )
      .afterClosed()
      .pipe(
        tap(() => this.forceDisplay.set(false)),
        filter((data: ContractReminderOverlayReturnedData) => data?.addReminder),
        tap((data: ContractReminderOverlayReturnedData) => this.addReminder.emit(data.external))
      )
      .subscribe();
  }

  private observeNextReminderDateChange(): void {
    this.subscription.add(
      this.contractReminderOverlayService.updateNextReminderDate$
        .pipe(
          filter((data: UpdateNextReminderDateData) => data.contractId === this.contractId),
          tap((data: UpdateNextReminderDateData) => this.updateNextReminderDate.emit(data.nextReminderDate))
        )
        .subscribe()
    );
  }
}
